import React, { FC } from 'react';
import styled, { css } from 'styled-components';

import { IconButton } from '../IconButton/IconButton';
import { NgTooltip } from '../NgTooltip/NgTooltip';
import { NgNavItemIcon } from './NgNavItemIcon';

type NavItemVariant = 'default' | 'sub';

const StyledNavItem = styled.li<{
  $expanded: boolean;
  $active?: boolean;
  $variant?: NavItemVariant;
  $highlightFull?: boolean;
}>`
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: 6px;
  padding: 4px;
  transition: background-color 0.15s ease;

  ${({ $active, $highlightFull }) =>
    $active &&
    $highlightFull &&
    css`
      background-color: #eef5ff;
    `}

  &:hover {
    background-color: ${({ $active, $highlightFull }) =>
      $active && $highlightFull ? '#eef5ff' : 'rgba(0, 0, 0, 0.04)'};
  }

  .label,
  .sub-label {
    margin-left: 10px;
    opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
    visibility: ${({ $expanded }) => ($expanded ? 'visible' : 'hidden')};
    transition: opacity 0.2s ease, visibility 0.2s ease;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .label {
    color: ${({ $active, $highlightFull }) => ($active && $highlightFull ? '#0060df' : '#333')};
    font-weight: ${({ $active, $highlightFull }) => ($active && $highlightFull ? 600 : 400)};
  }

  .sub-label {
    color: ${({ $active }) => ($active ? '#0060df' : '#555')};
    font-weight: ${({ $active }) => ($active ? 600 : 400)};
  }

  .caret {
    margin-left: auto;
    font-size: 0.8rem;
    transition: transform 0.2s ease;
    opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
    visibility: ${({ $expanded }) => ($expanded ? 'visible' : 'hidden')};
  }
  .caret--open {
    transform: rotate(90deg);
  }

  .no-outline-icon {
    svg {
      stroke: none;
    }
  }
`;

interface Props {
  item: any;
  icon?: React.ReactNode; // Custom icon - takes precedence over item.icon
  iconSize?: string; // ICON_SIZE
  isOpen?: boolean;
  expanded?: boolean;
  isParent?: boolean;
  active?: boolean; // isParentActive
  variant?: NavItemVariant; // 'default' | 'sub'
  highlightFull?: boolean; // Highlight whole row background vs just icon
  onClick: (data: any) => void; // handleNavItemClick
}

export const NgNavItem: FC<Props> = ({
  expanded = false,
  item,
  icon,
  active,
  isParent,
  isOpen,
  iconSize = '1.5rem',
  variant = 'default',
  highlightFull = false,
  onClick,
}) => {
  const labelClass = variant === 'sub' ? 'sub-label' : 'label';
  // Only highlight the icon if not using full row highlight
  const iconActive = active && !highlightFull;

  return (
    <StyledNavItem
      $expanded={expanded}
      $active={active}
      $variant={variant}
      $highlightFull={highlightFull}
      onClick={() => onClick(item)}
    >
      <NgTooltip title={item.displayName} placement="right" offset={16} disabled={expanded}>
        <span>
          <IconButton className="icon-button" active={iconActive} size={iconSize}>
            {icon || <NgNavItemIcon icon={item.icon} />}
          </IconButton>
        </span>
      </NgTooltip>

      {expanded && (
        <>
          <span className={labelClass}>{item.displayName}</span>
          {isParent && (
            <span className={`caret ${isOpen ? 'caret--open' : ''}`} aria-hidden>
              ▶
            </span>
          )}
        </>
      )}
    </StyledNavItem>
  );
};
